import { useMutation } from '@apollo/client';
import { FormProvider } from 'react-hook-form';
import { toast } from 'react-toastify';
import { graphql } from '~/apollo/generated/v4';
import { Modal } from '~/components/common/Modal';
import { Button } from '~/components/ui/button';
import { HookFormErrors } from '~/components/ui/forms/HookFormErrors';
import { LithostratLinkFormFieldsV4 } from '~/components/upload/lithostrat/lithostrat-link/lithostrat-link-form-fields';
import { useModalState } from '~/hooks/modal';
import { useZodForm } from '~/utils/forms';
import type { RefetchQueries } from '~/utils/graphql';
import {
  initialLithostratLink,
  lithostratLinkSchema,
} from '~/utils/modules/lithostrat';

export const CREATE_LITHOSTRAT_STUDY_LINK = graphql(`
  mutation CreateLithostratStudyLink($input: LithostratStudyLinkCreateInput!) {
    lithostratStudyLinkCreate(input: $input) {
      result {
        id
      }
    }
  }
`);

export function CreateLithostratStudyLinkModalV4({
  studyId,
  children,
  refetchQueries,
}: {
  studyId: number;
  children: (showModal: () => void) => React.ReactNode;
  refetchQueries: RefetchQueries;
}) {
  const { show, showModal, hideModal } = useModalState();

  const [createLink, { loading, error }] = useMutation(
    CREATE_LITHOSTRAT_STUDY_LINK,
    { refetchQueries },
  );

  const form = useZodForm({
    schema: lithostratLinkSchema,
    values: initialLithostratLink(),
  });

  const handleSubmit = form.handleSubmit(async values => {
    try {
      await createLink({
        variables: {
          input: {
            parentId: studyId,
            targetType: values.targetType,
            targetId: values.targetId,
          },
        },
      });
      toast.success('Link created successfully.');
      hideModal();
      form.reset(initialLithostratLink());
    } catch (err) {
      console.log('Error creating link', err);
      toast.error(
        'There was a problem creating the link. Check the data entered and try again.',
      );
    }
  });

  return (
    <>
      {children(showModal)}

      <Modal open={show} onHide={hideModal}>
        <FormProvider {...form}>
          <form onSubmit={handleSubmit}>
            <Modal.Body heading="Create Lithostrat Link">
              <div className="space-y-4">
                <LithostratLinkFormFieldsV4 />
                <HookFormErrors graphQLError={error} />
              </div>
            </Modal.Body>

            <Modal.Footer>
              <Button
                type="button"
                color="ghost"
                onClick={hideModal}
                disabled={loading}
              >
                Cancel
              </Button>
              <Button type="submit" color="primary" loading={loading}>
                Save
              </Button>
            </Modal.Footer>
          </form>
        </FormProvider>
      </Modal>
    </>
  );
}
